import { useLayoutContext } from '../Contexts/LayoutContext';
import CartService from '../Services/CartService';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import { useCartContext } from '../Contexts/CartContext';
import { useStoreContext } from '../Contexts/StoreContext';
import { useAddProductContext } from '../Contexts/AddProductContext';
import { PRODUCT_NOT_ACTIVE } from '../Utils/Constants/Product';
import { useTranslation } from 'react-i18next';

export const useProductsHook = () => {

  const { t } = useTranslation();
  const { setCartTemp, cartConference, setCartConference, returnToCheckout, setCurrentCart, setLoadingCurrentCart } = useCartContext();
  const { setMainLoading, setMainLoadingIcon, setMainLoadingIconSuccess, setMainLoadingIconError, setMainLoadingTitle, setMainLoadingMessage, setMainLoadingCloseButton, setMainLoadingIconErrorAddProduct, setMainLoadingAutoClose, setShowStart } = useLayoutContext();
  const { orderType, isDelivery } = useOrderTypeContext();
  const { product, setRedirectToProductSlug } = useAddProductContext();

  const { setLoadStoreInfoOnCloseMainLoading, currentAddress } = useStoreContext();
  const { setShowAddProduct } = useAddProductContext();

  const sendProductsStoreCounter = async (cart_id: number, payload: any) => {

    setMainLoading(true);
    setMainLoadingIcon(true);
    setMainLoadingCloseButton(false);
    setMainLoadingTitle(t('products.sending_items_title_store_counter'));
    setMainLoadingMessage(t('layout.loading_main.default_message'));

    //* Removendo produtos com estoque zerado da lista de conferência
    const reemoveItems = (products: any) => {
      products.unavailable_items.notallowed
        .filter((item: any) => item.product?.stock && item.product.stock[0]?.quantity <= 0 && !item.product.stock[0]?.allow_negative_stock)
        .map((item: any) => {
          return setCartConference(cartConference.filter((product: any) => {
            const idToCompare = product.complementable_id ?? product.id;
            return idToCompare !== item.product_id;
          }));
        });
    }

    //* Gerando mensagem de disponibilidade do produto
    const messageUnavailableReemovibleItems = (products: any): string => {
      const { notallowed } = products.unavailable_items;
      const { unavailableItems, removibleItems } = notallowed.reduce(
        (acc: { unavailableItems: string[]; removibleItems: string[] }, item: any) => {
          const { product } = item;
          const quantity = product?.stock?.[0]?.quantity ?? 0;
          const allowNegative = product?.stock?.[0]?.allow_negative_stock ?? true;

          if (quantity > 0) {
            acc.unavailableItems.push(`- ${product.name} (${quantity} ${quantity > 1 ? 'disponíveis' : 'disponível'})`);
          } else if (quantity <= 0 && !allowNegative) {
            acc.removibleItems.push(`- ${product.name}`);
          }
          return acc;
        },
        { unavailableItems: [], removibleItems: [] }
      );

      const unavailableItemsMessage = unavailableItems.length
        ? unavailableItems.join('<br />')
        : '';

      const removibleItemsMessage = removibleItems.length
        ? `
          <div class='font-semibold text-gray-500 mb-2'>
            ${removibleItems.length > 1
              ? t('products.unavailable_items_plural')
              : t('products.unavailable_items_singular')}
          </div>
          <span>${removibleItems.join('<br />')}</span>`
        : '';

      return [unavailableItemsMessage, removibleItemsMessage].filter(Boolean).join('<br /><br />');
    }

    try {
      const cartService = new CartService(orderType);
      const addedProduct = await cartService.addProduct(cart_id, payload);

      if (addedProduct.success) {
          setCartConference([]);
          setCartTemp([]);
          setMainLoadingIcon(false);
          setMainLoadingIconSuccess(true);
          setMainLoadingCloseButton(true);
          setMainLoadingTitle(returnToCheckout
            ? t('products.product_added_return_to_checkout_title')
            : `
              <div class='grid'>
                <span>${t('products.product_added_title')}</span>
                <span>${t('products.product_added_message')}</span>
                <span class='font-black text-2xl mt-1.5'>${payload.table_name}</span>
              </div>`
          );
          setMainLoadingMessage(t('products.product_added_message_2'));
          setMainLoadingAutoClose(true);
          return true;
      } else {
          setMainLoadingIcon(false);
          setMainLoadingIconErrorAddProduct(true);
          setMainLoadingCloseButton(true);
          setMainLoadingTitle(addedProduct.unavailable_items?.notallowed.length > 1 ? t('products.error_unavailable_items_title_plural') : t('products.error_unavailable_items_title'));
          setMainLoadingMessage(messageUnavailableReemovibleItems(addedProduct));
          reemoveItems(addedProduct);
          setLoadStoreInfoOnCloseMainLoading(true);
          return false;
      }

    } catch (error) {
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingIconErrorAddProduct(false);
      setMainLoadingTitle(t('errors.default_title'));
      setMainLoadingMessage(t('products.error_sending_items_title_store_counter'));
    }

  };

  const sendProducts = async (cart_id: number, payload: any) => {
    setShowAddProduct(false);
    setCartConference([]);
    setCartTemp([]);

    setLoadingCurrentCart(true);

    try {
      const cartService = new CartService(orderType);
      const cartWithAddedProduct = await cartService.addProduct(cart_id, payload);

      if (cartWithAddedProduct) {
          setCurrentCart(cartWithAddedProduct)
          setLoadingCurrentCart(false);
          return true;
      }
    } catch (error: any) {
      const errorTitle = error?.response?.data?.error === PRODUCT_NOT_ACTIVE ? t('products.error_product_not_active') : t('errors.default_title');
      setMainLoading(true);
      setMainLoadingIcon(false);
      setMainLoadingIconError(true);
      setMainLoadingCloseButton(true);
      setMainLoadingIconErrorAddProduct(false);
      setMainLoadingTitle(errorTitle);
      setMainLoadingMessage(t('products.error_add_product', { productName: product.name }));

      //*
      setCartConference([]);
      setCartTemp([]);
      setLoadingCurrentCart(false);
      throw error;
    }

  };

  /**
  * Verifica se existe um endereço atual selecionado.
  * @returns {boolean} Retorna `true` se houver um endereço atual`.
  */
  const hasCurrentAddress = (productSlug: string): boolean => {
    if (!currentAddress && isDelivery) {
      setShowStart(true);
      setRedirectToProductSlug(productSlug); //* armazenando slug do produto para redirecionamento após selecionar loja
      return false;
    }
    return true;
  }

  return { sendProductsStoreCounter, sendProducts, hasCurrentAddress };
};
